/**
 * Organization Call to Action Service
 * Handles CTA CRUD operations, URL building, and QR code generation for organizations
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type { Organization } from "../types/organization.types";
// R2B (2026-05-17): shared composite generator — see eventCTA.service for
// the parallel path and ctaQRComposite.service.ts for the implementation.
import { generateCompositeQRSVG } from "./ctaQRComposite.service";
import type {
  OrganizationCTA,
  CreateOrganizationCTAInput,
  UpdateOrganizationCTAInput,
  AvailableOffer,
  OrganizationCTAType,
} from "../types/organizationCTA.types";
import { SUBSCRIPTION_CTA_BASE_URL } from "../types/organizationCTA.types";

// Storage bucket for organization CTA QR codes
const ORG_CTA_QR_BUCKET = "organization-cta-qr-codes";

// =====================================================
// CTA CRUD OPERATIONS
// =====================================================

/**
 * Get all CTAs for an organization
 */
export async function getOrganizationCTAs(
  organizationId: UUID,
): Promise<OrganizationCTA[]> {
  const { data, error } = await supabase
    .from("organization_ctas")
    .select(
      `
      *,
      offer:offers(id, name, offer_code, offer_type, value_type, value_amount, description),
      location:locations(id, name, state, city, affiliate_id)
    `,
    )
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching organization CTAs:", error);
    throw error;
  }

  return data || [];
}

/**
 * Get a single CTA by ID
 */
export async function getOrganizationCTA(
  ctaId: UUID,
): Promise<OrganizationCTA | null> {
  const { data, error } = await supabase
    .from("organization_ctas")
    .select(
      `
      *,
      offer:offers(id, name, offer_code, offer_type, value_type, value_amount, description),
      location:locations(id, name, state, city, affiliate_id)
    `,
    )
    .eq("id", ctaId)
    .single();

  if (error) {
    console.error("Error fetching organization CTA:", error);
    throw error;
  }

  return data;
}

/**
 * Create a new organization CTA
 */
export async function createOrganizationCTA(
  input: CreateOrganizationCTAInput,
  userId: UUID,
): Promise<OrganizationCTA> {
  const { data, error } = await supabase
    .from("organization_ctas")
    .insert({
      organization_id: input.organization_id,
      location_id: input.location_id || null,
      name: input.name,
      cta_type: input.cta_type,
      base_url: input.base_url || null,
      parameters: input.parameters || {},
      reward_type: input.reward_type || "none",
      offer_id: input.offer_id || null,
      voucher_amount: input.voucher_amount || null,
      voucher_start_date: input.voucher_start_date || null,
      voucher_end_date: input.voucher_end_date || null,
      voucher_expiry_days: input.voucher_expiry_days ?? 30,
      use_member_signup: input.use_member_signup || false,
      display_away_plans: input.display_away_plans || false,
      // Defaults FALSE: every QR code already printed carries discountvalue in its URL.
      allow_variable_voucher_value: input.allow_variable_voucher_value || false,
      // Defaults to true for backwards compat; false only when the operator
      // unchecked "Include state parameter" on a subscription CTA. Was previously
      // omitted from the insert entirely, so a new CTA always reverted to the
      // column default (true) regardless of the checkbox — the save "reverting" bug.
      include_state_param: input.include_state_param !== false,
      // Opt-in flags are written explicitly: omitted here, they silently fall back to the column
      // default -- which is how "Use State Defaults" was lost on every newly created CTA.
      use_state_defaults: input.use_state_defaults === true,
      single_voucher: input.single_voucher === true,
      // R2B (2026-05-17): optional rep attribution
      sales_position_id: input.sales_position_id || null,
      // MSR EVT-04 (mig 530): written only when chosen, so a create without the picker keeps the column default (NULL = any).
      ...(input.activity_type ? { activity_type: input.activity_type } : {}),
      created_by: userId,
      updated_by: userId,
    })
    .select(
      `
      *,
      offer:offers(id, name, offer_code, offer_type, value_type, value_amount, description),
      location:locations(id, name, state, city, affiliate_id)
    `,
    )
    .single();

  if (error) {
    console.error("Error creating organization CTA:", error);
    throw error;
  }

  return data;
}

/**
 * Update an existing organization CTA
 */
export async function updateOrganizationCTA(
  ctaId: UUID,
  updates: UpdateOrganizationCTAInput,
  userId: UUID,
): Promise<OrganizationCTA> {
  const { data, error } = await supabase
    .from("organization_ctas")
    .update({
      ...updates,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    })
    .eq("id", ctaId)
    .select(
      `
      *,
      offer:offers(id, name, offer_code, offer_type, value_type, value_amount, description),
      location:locations(id, name, state, city, affiliate_id)
    `,
    )
    .single();

  if (error) {
    console.error("Error updating organization CTA:", error);
    throw error;
  }

  return data;
}

/**
 * Delete an organization CTA
 */
export async function deleteOrganizationCTA(ctaId: UUID): Promise<void> {
  // Org-CTA lockdown audit H1 (2026-10-02): the QR asset used to be removed BEFORE the row delete, and the
  // delete only checked `error`, so a role the database refuses (RLS hides the row -> 0 rows, no error) could
  // still destroy a live CTA's public QR image. Same order as deleteEventCTA now: read the asset path ->
  // delete the ROW and confirm exactly one row went -> only then remove the asset.
  const { data: cta } = await supabase
    .from("organization_ctas")
    .select("qr_code_url")
    .eq("id", ctaId)
    .maybeSingle();

  const { data: deleted, error } = await supabase
    .from("organization_ctas")
    .delete()
    .eq("id", ctaId)
    .select("id");

  let rowGone: boolean;
  if (error) {
    if ((error as { code?: string }).code) {
      // An explicit server refusal: nothing was deleted; the asset stays with its row.
      console.error("Error deleting organization CTA:", error);
      throw error;
    }
    // No server answer: find out whether the delete happened before touching the asset.
    const { data: still, error: checkError } = await supabase
      .from("organization_ctas")
      .select("id")
      .eq("id", ctaId)
      .maybeSingle();
    rowGone = !checkError && !still;
    if (!rowGone) {
      throw error; // the row is still there, or we can't tell: keep its QR asset
    }
  } else {
    rowGone = (deleted ?? []).length === 1;
    if (!rowGone) {
      // RLS filtered the delete (or the row was already gone): don't touch an asset we can't account for.
      throw new Error(
        "You don't have permission to delete this Call to Action.",
      );
    }
  }

  if (rowGone && cta?.qr_code_url) {
    try {
      const fileName = extractFileNameFromUrl(cta.qr_code_url);
      if (fileName) {
        await supabase.storage.from(ORG_CTA_QR_BUCKET).remove([fileName]);
      }
    } catch (err) {
      // The row is gone; a leftover file is harmless litter.
      console.warn("Failed to delete QR code from storage:", err);
    }
  }
}

// =====================================================
// OFFER FETCHING
// =====================================================

/**
 * Get offers available for an organization
 * Uses the get_offers_for_organization database function
 */
export async function getOffersForOrganization(
  organizationId: UUID,
): Promise<AvailableOffer[]> {
  const { data, error } = await supabase.rpc("get_offers_for_organization", {
    p_organization_id: organizationId,
  });

  if (error) {
    console.error("Error fetching offers for organization:", error);
    throw error;
  }

  return data || [];
}

// =====================================================
// URL BUILDING
// =====================================================

/**
 * Build the full URL for an organization CTA based on its type and parameters
 *
 * Auto-populated parameters for subscription CTAs:
 * - ste: Organization/location state (2-char code) - uses location state if CTA has a location_id
 * - discountvalue: From voucher_amount or linked offer's value_amount — OMITTED entirely
 *   when allow_variable_voucher_value is set, so the value can change without reprinting
 *   the QR code (the landing page then calls the cta-voucher-value edge function)
 */
export function buildOrganizationCTAUrl(
  cta: {
    id?: UUID;
    cta_type: OrganizationCTAType;
    base_url?: string;
    parameters: Record<string, string>;
    offer?: { value_amount?: number } | null;
    voucher_amount?: number;
    /** When true the URL omits discountvalue; the landing page calls cta-voucher-value. */
    allow_variable_voucher_value?: boolean;
    // Location join carries the location's `state` (see CTALocationSummary);
    // organizations use `address_state`. The builder reads cta.location?.state,
    // matching the event CTA builder's `event.state` convention.
    location?: { state?: string } | null;
    include_state_param?: boolean;
    /** When true the URL carries usestatedefaults=1. Defaults OFF: every CTA that already exists,
     *  including printed QR codes, must keep producing the same URL. */
    use_state_defaults?: boolean;
    /** When true the URL carries singlevoucher=1. Defaults OFF (organization CTAs are always voucher/offer). */
    single_voucher?: boolean;
    use_member_signup?: boolean;
    display_away_plans?: boolean;
  },
  organization: { id: UUID; address_state?: string },
): string {
  const params = new URLSearchParams();

  let baseUrl: string;

  if (cta.cta_type === "subscription") {
    // Use member-signup URL if bypass checkbox is checked
    if (cta.use_member_signup) {
      baseUrl = "https://ww2.uslawshield.com/member-signup/";
    } else {
      // Always use environment variable for subscription CTAs
      baseUrl = SUBSCRIPTION_CTA_BASE_URL;
    }

    // Add auto-populated parameters
    // include_state_param defaults to true for backwards compatibility
    // Use location's state if CTA has a location, otherwise use organization's state
    const stateToUse = cta.location?.state || organization.address_state;
    if (stateToUse && cta.include_state_param !== false) {
      params.append("ste", stateToUse.toUpperCase());
    }

    // Add CTA ID for tracking
    if (cta.id) {
      params.append("ctaid", cta.id);
    }

    // Add discount value from voucher amount or linked offer.
    //
    // ...UNLESS this CTA allows the value to vary. Then the amount is deliberately left out
    // of the URL so one printed QR code survives a seasonal or promotional change, and the
    // landing page asks the cta-voucher-value edge function at scan time instead. The
    // resolution order below is mirrored exactly by that function; if you change one, change
    // both, or the URL and the endpoint will disagree about the same CTA.
    if (!cta.allow_variable_voucher_value) {
      if (cta.voucher_amount && cta.voucher_amount > 0) {
        params.append("discountvalue", String(cta.voucher_amount));
      } else if (cta.offer?.value_amount && cta.offer.value_amount > 0) {
        params.append("discountvalue", String(cta.offer.value_amount));
      }
    }

    // Add user-defined parameters
    Object.entries(cta.parameters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    // Add away plans parameter if checked
    if (cta.display_away_plans) {
      params.append("plantype", "away");
    }

    // ⭐ Opt-in only. `=== true`, not `!== false`, because this flag defaults OFF — the opposite of
    // include_state_param. Reading it the other way would silently add the parameter to all 2,683
    // existing organization CTAs.
    if (cta.use_state_defaults === true) {
      params.append("usestatedefaults", "1");
    }

    // Opt-in only (owner 2026-09-29). Organization CTAs are always voucher/offer rewards.
    if (cta.single_voucher === true) {
      params.append("singlevoucher", "1");
    }

    // Retail flag — fixed value for organization voucher CTAs
    params.append("retail", "1");
  } else {
    // For 'other' type, use the stored base_url
    baseUrl = cta.base_url || "";

    // Add all parameters
    Object.entries(cta.parameters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
  }

  const queryString = params.toString();
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;

  return queryString ? `${cleanBaseUrl}?${queryString}` : cleanBaseUrl;
}

/**
 * Get the default base URL for a CTA type
 */
export function getDefaultBaseUrl(ctaType: OrganizationCTAType): string {
  return ctaType === "subscription" ? SUBSCRIPTION_CTA_BASE_URL : "";
}

// =====================================================
// QR CODE GENERATION
// =====================================================

// generateQRCodeSVG removed in R2B (2026-05-17). The shared composite
// generator (generateCompositeQRSVG) replaces it — produces a QR PLUS
// rep name + org identity labels in one SVG asset. See
// crm-frontend/src/services/ctaQRComposite.service.ts.

/**
 * Upload QR code to Supabase storage
 */
async function uploadQRCode(
  organizationId: UUID,
  ctaId: UUID,
  svgContent: string,
): Promise<string> {
  const fileName = `${organizationId}/${ctaId}-${Date.now()}.svg`;
  const blob = new Blob([svgContent], { type: "image/svg+xml" });

  const { error: uploadError } = await supabase.storage
    .from(ORG_CTA_QR_BUCKET)
    .upload(fileName, blob, {
      contentType: "image/svg+xml",
      cacheControl: "3600",
      upsert: true,
    });

  if (uploadError) {
    console.error("Error uploading QR code:", uploadError);
    throw new Error(`Failed to upload QR code: ${uploadError.message}`);
  }

  const { data: urlData } = supabase.storage
    .from(ORG_CTA_QR_BUCKET)
    .getPublicUrl(fileName);

  return urlData.publicUrl;
}

/**
 * Extract filename from Supabase storage URL
 */
function extractFileNameFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/");
    const bucketIndex = pathParts.indexOf(ORG_CTA_QR_BUCKET);
    if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
      return pathParts.slice(bucketIndex + 1).join("/");
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Generate QR code for an organization CTA and store it.
 *
 * R2B (2026-05-17): mirrors the event CTA path. Uses the shared
 * composite generator (QR + rep name + org label). Upload-first
 * ordering per audit must-fix to avoid orphaning the public URL when
 * a DB update fails after a successful upload.
 */
export async function generateOrganizationCTAQRCode(
  ctaId: UUID,
  organization: Organization,
  userId: UUID,
): Promise<OrganizationCTA> {
  const cta = await getOrganizationCTA(ctaId);
  if (!cta) {
    throw new Error("CTA not found");
  }

  // Pull rep info for the overlay (if any). Mirrors the event path.
  let repName: string | null = null;
  if (cta.sales_position_id) {
    const { data: pos } = await supabase
      .from("sales_positions")
      .select("assigned_name, title, is_active")
      .eq("id", cta.sales_position_id)
      .single();
    if (pos) {
      const name = pos.assigned_name || pos.title;
      repName = pos.is_active === false ? `${name} (inactive)` : name;
    }
  }

  const generatedUrl = buildOrganizationCTAUrl(cta, organization);
  const svgContent = await generateCompositeQRSVG({
    url: generatedUrl,
    repName,
    contextLabel: organization.name,
  });

  // STEP 1: upload new asset (timestamped filename cache-busts old).
  const newQrUrl = await uploadQRCode(cta.organization_id, ctaId, svgContent);

  // STEP 2: update DB. On failure, clean up the orphan upload and rethrow.
  const oldQrUrl = cta.qr_code_url;
  let updatedCTA: OrganizationCTA;
  try {
    updatedCTA = await updateOrganizationCTA(
      ctaId,
      {
        generated_url: generatedUrl,
        qr_code_url: newQrUrl,
      },
      userId,
    );
  } catch (dbErr) {
    try {
      const orphanFileName = extractFileNameFromUrl(newQrUrl);
      if (orphanFileName) {
        await supabase.storage.from(ORG_CTA_QR_BUCKET).remove([orphanFileName]);
      }
    } catch (cleanupErr) {
      console.warn(
        "Failed to clean up orphaned QR upload after DB error:",
        cleanupErr,
      );
    }
    throw dbErr;
  }

  // STEP 3: best-effort delete old asset.
  if (oldQrUrl && oldQrUrl !== newQrUrl) {
    try {
      const oldFileName = extractFileNameFromUrl(oldQrUrl);
      if (oldFileName) {
        await supabase.storage.from(ORG_CTA_QR_BUCKET).remove([oldFileName]);
      }
    } catch (err) {
      console.warn("Failed to delete old QR code (orphan only):", err);
    }
  }

  return updatedCTA;
}

// =====================================================
// VALIDATION
// =====================================================

/**
 * Validate subscription CTA parameters
 * Returns array of missing required fields
 */
export function validateSubscriptionCTAParams(
  params: Record<string, string>,
): string[] {
  const requiredFields = [
    { key: "affid", label: "Promoter Affiliate ID" },
    { key: "promo", label: "Promo" },
    { key: "altprice", label: "Alt Price" },
    { key: "utm_source", label: "UTM Source" },
    { key: "utm_term", label: "UTM Term" },
    { key: "utm_campaign", label: "UTM Campaign" },
    { key: "utm_medium", label: "UTM Medium" },
  ];

  const missing: string[] = [];
  for (const field of requiredFields) {
    if (!params[field.key] || params[field.key].trim() === "") {
      missing.push(field.label);
    }
  }

  return missing;
}

/**
 * Validate other CTA (requires base URL)
 */
export function validateOtherCTA(baseUrl: string | undefined): boolean {
  if (!baseUrl || baseUrl.trim() === "") {
    return false;
  }
  try {
    new URL(baseUrl);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if organization has required state for subscription CTAs
 */
export function organizationHasRequiredState(
  organization: Organization,
): boolean {
  return Boolean(
    organization.address_state && organization.address_state.length === 2,
  );
}
